import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import authService from '../services/authService';

/**
 * Request Invitation Component
 * Allows users to request an invitation to sign up, which will be reviewed by an admin
 */
const RequestInvitation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    reason: '',
    phoneNumber: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate form data
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Call the API to request an invitation
      const response = await authService.requestInvitation(formData);
      
      if (response.success) {
        setSubmitted(true);
        toast.success('Invitation request submitted successfully!');
      } else {
        toast.error(response.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting invitation request:', error);
      
      // Special handling for rate limiting errors
      if (error.response?.status === 429 || 
          error.message?.includes('rate limit') ||
          error.message?.includes('too many requests')) {
        toast.error('Too many requests. Please try again later.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to submit request. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success message after submission
  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
        <div className="bg-white bg-opacity-95 backdrop-blur-sm p-8 rounded-3xl shadow-xl w-full max-w-md mx-4">
          <h2 className="text-2xl font-semibold text-center mb-4 text-[#6C5DD3]">Request Submitted</h2>
          
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <p>Thank you for your interest in MnyMny!</p>
            <p className="mt-2">Your invitation request has been submitted and is awaiting admin approval. We'll send you an email once your request has been reviewed.</p>
          </div>
          
          <div className="text-center">
            <Link 
              to="/login" 
              className="inline-block px-6 py-3 bg-[#6C5DD3] text-white rounded-xl hover:bg-[#5849B6] transition duration-300"
            >
              Return to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
      <div className="bg-white bg-opacity-95 backdrop-blur-sm p-8 rounded-3xl shadow-xl w-full max-w-md mx-4">
        <h2 className="text-2xl font-semibold text-center mb-2 text-[#6C5DD3]">Request an Invitation</h2>
        <p className="text-center text-gray-600 mb-6">
          Please fill out this form to request access to MnyMny. An administrator will review your request.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-gray-600 text-sm" htmlFor="firstName">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
                placeholder="Enter first name"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-gray-600 text-sm" htmlFor="lastName">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
                placeholder="Enter last name"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
              placeholder="Enter your email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="phoneNumber">
              Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <input
              id="phoneNumber"
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
              placeholder="Enter your phone number (optional)"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="reason">
              Why do you want to join MnyMny?
            </label>
            <textarea
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
              placeholder="Please briefly explain why you'd like to use MnyMny..."
              required
              disabled={isSubmitting}
            ></textarea>
          </div>

          <div className="flex items-center justify-between">
            <Link 
              to="/login" 
              className="text-[#6C5DD3] hover:text-[#5849B6] text-sm"
            >
              Back to login
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-[#6C5DD3] text-white rounded-xl hover:bg-[#5849B6] transition duration-300 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestInvitation; 